import React, { useEffect, useState } from 'react';
import PlacesAutocomplete, {
    geocodeByPlaceId,
    getLatLng,
} from 'react-places-autocomplete';

interface IGoogleSearchPlaceInputV2 {
    className?: string;
    onSelect: (data: any) => void;
    selectedAddress?: string;
    onChange?: (address: string) => void;
    onDelete?: () => void;
    showDelete?: boolean;
    inputStyle?: React.CSSProperties;
    label?: string;
    placeholder?: string;
    errorMessage?: string;
}

const GoogleSearchPlaceInputV2: React.FC<IGoogleSearchPlaceInputV2> = (props) => {
    const {
        selectedAddress = '',
        label,
        placeholder = 'Search Location',
        errorMessage,
        showDelete,
        onDelete,
        className,
        inputStyle,
    } = props;

    const [searchValue, setSearchValue] = useState(selectedAddress);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        setSearchValue(selectedAddress);
    }, [selectedAddress]);

    const onSelect = async (address: string, placeId: string) => {
        const response = await geocodeByPlaceId(placeId);
        if (response?.length > 0) {
            const details: any = response[0];
            const { lat, lng } = await getLatLng(details);
            const obj: any = {
                address,
                coordinates: [lat, lng],
                country: '',
                city: '',
                post_code: '',
            };
            details?.address_components?.forEach((item: any) => {
                if (item?.types?.[0] === 'country')
                    obj.country = item?.long_name || item?.short_name;
                if (item?.types?.[0] === 'administrative_area_level_1')
                    obj.city = item?.long_name || item?.short_name;
                if (item?.types?.[0] === 'postal_code')
                    obj.post_code = item?.long_name || item?.short_name;
            });
            setSearchValue(address);
            props.onSelect(obj);
        }
    };

    const getBoxClass = () => {
        if (errorMessage) return 'input-field-error';
        if (isFocused) return 'input-field-focused';
        return '';
    };

    return (
        <div className={`input-field-wrapper gsp-wrapper ${className || ''}`}>

            {/* Label — same as InputFieldV2 */}
            {label && (
                <div className="input-field-label-row">
                    <label className="input-field-label">{label}</label>
                </div>
            )}

            <PlacesAutocomplete
                value={searchValue}
                onChange={value => {
                    setSearchValue(value);
                    props.onChange?.(value);
                }}
                onSelect={onSelect}
            >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div className="gsp-autocomplete">

                        {/* Input box — same as InputFieldV2 */}
                        <div className={`input-field-box gsp-box ${getBoxClass()}`}>
                            {/* Pin icon */}
                            <svg
                                className="gsp-pin-icon"
                                width="14" height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            >
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>

                            <input
                                style={inputStyle}
                                {...getInputProps({
                                    placeholder,
                                    className: 'input-field-input gsp-input',
                                    onFocus: () => setIsFocused(true),
                                    onBlur: () => setIsFocused(false),
                                })}
                            />

                            {/* Clear button when has value */}
                            {searchValue && !isFocused && (
                                <button
                                    type="button"
                                    className="gsp-clear"
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        setSearchValue('');
                                        props.onChange?.('');
                                    }}
                                    aria-label="Clear"
                                >
                                    <img
                                        src="/assets/images/cross.svg"
                                        alt="clear"
                                        className="input-field-clear-icon"
                                        style={{ position: 'static', width: 14, height: 14 }}
                                    />
                                </button>
                            )}

                            {/* Delete button */}
                            {showDelete && (
                                <button
                                    type="button"
                                    className="gsp-delete"
                                    onClick={onDelete}
                                    aria-label="Delete location"
                                >
                                    <img
                                        src="/assets/images/delete-fill-inner-red.svg"
                                        alt="delete"
                                        width="16"
                                        height="16"
                                    />
                                </button>
                            )}
                        </div>

                        {/* Dropdown suggestions */}
                        {(loading || suggestions.length > 0) && (
                            <div className="gsp-dropdown">
                                {loading && (
                                    <div className="gsp-dropdown__loading">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="2">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                        Searching…
                                    </div>
                                )}
                                {suggestions.map((suggestion, i: any) => (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            className: `gsp-dropdown__item${suggestion.active ? ' gsp-dropdown__item--active' : ''}`,
                                        })}
                                    >
                                        {/* Pin icon per suggestion */}
                                        <svg
                                            className="gsp-dropdown__icon"
                                            width="13" height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        >
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        <span className="gsp-dropdown__text">
                                            {suggestion.description}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </PlacesAutocomplete>

            {/* Error message — same as InputFieldV2 */}
            {errorMessage && (
                <span className="input-field-error-text">{errorMessage}</span>
            )}
        </div>
    );
};

export default GoogleSearchPlaceInputV2;